import {
  Box,
  Button,
  Checkbox,
  Grid,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { Eye, ReceiptEditIcon } from 'asset';
import {
  HeadTable,
  PermissionWrapper,
  PopupCreateEditSurcharge,
  PopupGenerateJobs,
  PopupGeneratePastJobs,
  TableCaption,
} from 'components';
import PopupDetailJobs from 'components/Popups/Jobs/PopupDetailJobs';
import PopupEditACJobs from 'components/Popups/Jobs/PopupEditACJobs';
import { ButtonEdit } from 'components/common/table';
import { convertTimeToLocal, getSortItem, hasPermission } from 'helpers';
import { useDateTime } from 'hooks';
import {
  ECLOSE_POPUP,
  ENUM_ORDER,
  GET_JOBS_PARAMS_REQUEST,
  JOBS_STATUS,
  JOBS_TYPE,
} from 'models';
import { JOB_AC_PROPS } from 'models/subscription';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setParentJobsParams } from 'store/reducers/accounts';
import { getParentJobsAction } from 'store/reducers/accounts/actionTypes';
import { getAdjustmentSurchargeAction } from 'store/reducers/jobs/actionTypes';
import { STATUS_COLOR, headCells } from './data';

interface Props {
  parentId: string | undefined;
  dataCreateAC?: JOB_AC_PROPS;
}
const ACJobList = ({ parentId, dataCreateAC }: Props) => {
  const { formatDateTime } = useDateTime();
  const { jobs } = useAppSelector(
    (state) => state.accountReducer.parents.parent_detail_tab
  );
  const { subscription_detail } = useAppSelector(
    (state) => state.subscriptionReducer.subscription
  );
  const { isAllowCreateTrialJob } = useAppSelector(
    (state) => state.jobsReducer
  );

  const { params } = jobs;

  const dispatch = useAppDispatch();
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [openDetailPopup, setOpenDetailPopup] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [openAdjustment, setOpenAdjustment] = useState(false);
  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [openCreatePastJobPopup, setOpenCreatePastJobPopup] = useState(false);
  const [itemAction, setItemAction] = useState<JOBS_TYPE>();
  const [isGenerateJob, setIsGenerateJob] = useState<boolean>(false);

  const onSetListParams = (params: GET_JOBS_PARAMS_REQUEST) => {
    const newParams = { ...params, is_ac_job: true };
    dispatch(setParentJobsParams(newParams));
  };

  useEffect(() => {
    onSetListParams({ ...params, sort: 'start_datetime-desc' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (parentId) {
      dispatch(
        getParentJobsAction(
          parentId,
          {
            ...params,
            is_ac_job: true,
          },
          true
        )
      );
    }
  }, [params, parentId, dispatch]);

  const onReloadList = () => {
    if (!parentId) return;
    dispatch(
      getParentJobsAction(parentId, {
        ...params,
        is_ac_job: true,
      })
    );
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const { items } = usePagination({
    count: jobs.metadata.total_pages,
    page: jobs.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const sortItem = useMemo(() => {
    return getSortItem(params?.sort);
  }, [params]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = jobs?.records?.map((n: any) => n?.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_: any, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleRequestSort = (_: any, property: keyof JOBS_TYPE) => {
    setSelected([]);
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const onClosePopup = (type: ECLOSE_POPUP) => {
    switch (type) {
      case ECLOSE_POPUP.CREATE_OR_EDIT:
        setOpenEditPopup(false);
        setOpenCreatePopup(false);
        setItemAction(undefined);
        break;
      case ECLOSE_POPUP.DETAIL:
        setOpenDetailPopup(false);
        setItemAction(undefined);
        break;
      case ECLOSE_POPUP.ADJUSTMENT:
        setOpenAdjustment(false);
        setItemAction(undefined);
        break;
      default:
        setItemAction(undefined);
        break;
    }
  };

  const setOpenEdit = (event: any, data: JOBS_TYPE) => {
    event.stopPropagation();
    setItemAction(data);
    setOpenEditPopup(true);
  };

  const setOpenCreate = () => {
    setOpenCreatePopup(true);
  };

  const handleEditAdjustment = (data: JOBS_TYPE) => {
    dispatch(
      getAdjustmentSurchargeAction({
        job_id: data.id,
        sort: `createdAt-${ENUM_ORDER.ASC}`,
      })
    );
    setOpenAdjustment(true);
    setItemAction(data);
  };

  return (
    <>
      <Box display="flex" mb={2} gap={2}>
        <PermissionWrapper allowedRoles={['superadmin', 'staff']}>
          <Button
            variant="contained"
            sx={{
              height: 30,
              fontWeight: 600,
              fontSize: '13px',
              lineHeight: '100%',
            }}
            onClick={() => {
              setOpenCreate();
              setIsGenerateJob(false);
            }}
            disabled={
              !isAllowCreateTrialJob ||
              !dataCreateAC?.baseRequest?.ac_base_request_sitters?.some(
                (item) => item.status === 'accepted'
              )
            }
          >
            Generate trial job
          </Button>
        </PermissionWrapper>
        <PermissionWrapper allowedRoles={['superadmin', 'staff']}>
          <Button
            variant="contained"
            sx={{
              height: 30,
              fontWeight: 600,
              fontSize: '13px',
              lineHeight: '100%',
            }}
            onClick={() => {
              setOpenCreate();
              setIsGenerateJob(true);
            }}
            disabled={
              subscription_detail?.is_trial ||
              !dataCreateAC?.baseRequest?.ac_base_request_sitters?.some(
                (item) => item.status === 'accepted'
              )
            }
          >
            Generate job
          </Button>
        </PermissionWrapper>
        <PermissionWrapper allowedRoles={['superadmin', 'staff']}>
          <Button
            variant="contained"
            sx={{
              height: 30,
              fontWeight: 600,
              fontSize: '13px',
              lineHeight: '100%',
            }}
            onClick={() => {
              setOpenCreatePastJobPopup(true);
              setIsGenerateJob(true);
            }}
            disabled={
              subscription_detail?.is_trial ||
              !dataCreateAC?.baseRequest?.ac_base_request_sitters?.some(
                (item) => item.status === 'accepted'
              )
            }
          >
            Generate past job
          </Button>
        </PermissionWrapper>
      </Box>
      <Stack spacing={1}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 540px)',
            minHeight: '200px',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<JOBS_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={jobs?.records.length}
              headCells={headCells}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {jobs?.records &&
                jobs?.records?.map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  const jobExtendPending = row?.JobExtensions?.find(
                    (item) => item.status === 'pending'
                  );

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                    >
                      <TableCell
                        padding="checkbox"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                        }}
                      >
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(e) => handleClick(e, row.id)}
                          inputProps={{
                            'aria-labelledby': labelId,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {row.number}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {formatDateTime(convertTimeToLocal(row.start_datetime))}
                        &nbsp;-&nbsp;
                        {formatDateTime(convertTimeToLocal(row.end_datetime))}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {row.address?.street} {row.address?.name}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {row.Sitter.first_name}&nbsp;{row.Sitter?.last_name}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        ${row.sitter_earning.toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        $
                        {(
                          (jobExtendPending
                            ? row.total + +jobExtendPending.total
                            : row.total) +
                          (row?.job_surcharges.length > 0
                            ? row?.job_surcharges?.reduce(
                                (total, item) => total + item.parent_rate,
                                0
                              )
                            : 0)
                        ).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        <Grid
                          container
                          alignItems="center"
                          textTransform="capitalize"
                          flexWrap="nowrap"
                        >
                          <Grid
                            sx={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              marginRight: '5px',
                              flexWrap: 'nowrap',
                              backgroundColor:
                                STATUS_COLOR[row.status as JOBS_STATUS],
                            }}
                          />
                          <Typography
                            sx={{
                              height: '20px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.status}
                          </Typography>
                        </Grid>
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                        }}
                      >
                        <Stack direction="row" spacing="10px">
                          <Link to={`/jobs/${row.id}`}>
                            <Button
                              sx={{
                                minWidth: '38px !important',
                                height: '32px',
                                '&.MuiButton-root': {
                                  p: '0px !important',
                                },
                              }}
                              variant="contained"
                              color="secondary"
                            >
                              <Eye />
                            </Button>
                          </Link>
                          {hasPermission(['superadmin', 'staff']) && (
                            <>
                              <ButtonEdit
                                onClick={(e) => setOpenEdit(e, row)}
                              />
                              <Button
                                sx={{
                                  minWidth: '38px !important',
                                  height: '32px',
                                  '&.MuiButton-root': {
                                    p: '0px !important',
                                  },
                                }}
                                variant="contained"
                                color="secondary"
                                onClick={() => handleEditAdjustment(row)}
                              >
                                <ReceiptEditIcon />
                              </Button>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={jobs?.metadata}
          items={items}
        />
        {openDetailPopup && (
          <PopupDetailJobs
            id={itemAction?.id}
            open={openDetailPopup}
            handleClosePopup={() => onClosePopup(ECLOSE_POPUP.DETAIL)}
          />
        )}
        {openCreatePopup && (
          <PopupGenerateJobs
            open={openCreatePopup}
            dataCreateAC={dataCreateAC}
            onSuccess={onReloadList}
            handleClosePopup={() => onClosePopup(ECLOSE_POPUP.CREATE_OR_EDIT)}
            isGenerateJob={isGenerateJob}
          />
        )}
        {openCreatePastJobPopup && (
          <PopupGeneratePastJobs
            open={openCreatePastJobPopup}
            dataCreateAC={dataCreateAC}
            onSuccess={onReloadList}
            handleClosePopup={() => setOpenCreatePastJobPopup(false)}
            isGenerateJob={isGenerateJob}
          />
        )}
        {openEditPopup && (
          <PopupEditACJobs
            is_ac_job
            open={openEditPopup}
            jobId={itemAction?.id}
            onSuccess={onReloadList}
            handleClosePopup={() => onClosePopup(ECLOSE_POPUP.CREATE_OR_EDIT)}
          />
        )}
        {openAdjustment && (
          <PopupCreateEditSurcharge
            open={openAdjustment}
            onClosePopup={() => onClosePopup(ECLOSE_POPUP.ADJUSTMENT)}
            jobId={itemAction?.id ?? ''}
          />
        )}
      </Stack>
    </>
  );
};

export default ACJobList;
